import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, Timestamp, orderBy, addDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';

interface AccountRequest {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  position: string;
  phoneNumber?: string;
  reason?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  requestedAt: Timestamp;
  reviewedAt?: Timestamp;
  reviewedBy?: string;
  adminNotes?: string;
}

interface Empresa {
  id: string;
  nombre: string;
  codigo: string; // Para usar en la app (ej: "marbar", "tecpetrol")
  createdAt: Timestamp;
  createdBy: string;
}

interface AccountRequestsSectionProps {
  userEmail?: string;
}

interface AccountDeletionRequest {
  id: string;
  username: string;
  requestDate: Timestamp;
  status: 'pending' | 'approved' | 'rejected';
  processedDate?: Timestamp;
  processedBy?: string;
  adminNotes?: string;
}

export const AccountRequestsSection: React.FC<AccountRequestsSectionProps> = ({ userEmail }) => {
  const [activeTab, setActiveTab] = useState<'requests' | 'empresas' | 'deletions'>('requests');
  const [requests, setRequests] = useState<AccountRequest[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [deletionRequests, setDeletionRequests] = useState<AccountDeletionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED'>('PENDING');
  const [selectedRequest, setSelectedRequest] = useState<AccountRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  // Empresa form state
  const [newEmpresa, setNewEmpresa] = useState({ nombre: '', codigo: '' });
  const [editingEmpresa, setEditingEmpresa] = useState<string | null>(null);
  const [editEmpresaData, setEditEmpresaData] = useState({ nombre: '', codigo: '' });

  // Load account requests
  useEffect(() => {
    const q = query(
      collection(db, 'accountRequests'),
      orderBy('requestedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requestsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AccountRequest[];

      setRequests(requestsData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching account requests:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load empresas
  useEffect(() => {
    const q = query(
      collection(db, 'empresas'),
      orderBy('nombre', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const empresasData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Empresa[];

      setEmpresas(empresasData);
    }, (error) => {
      console.error('Error fetching empresas:', error);
    });

    return () => unsubscribe();
  }, []);

  // Load account deletion requests
  useEffect(() => {
    const q = query(
      collection(db, 'accountDeletionRequests'),
      orderBy('requestDate', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const deletionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AccountDeletionRequest[];

      setDeletionRequests(deletionsData);
    }, (error) => {
      console.error('Error fetching deletion requests:', error);
    });

    return () => unsubscribe();
  }, []);

  // Empresas CRUD functions
  const handleAddEmpresa = async () => {
    if (!newEmpresa.nombre.trim() || !newEmpresa.codigo.trim()) {
      alert('Por favor completa todos los campos');
      return;
    }

    if (!userEmail) {
      alert('Error: No se pudo determinar el usuario actual');
      return;
    }

    try {
      await addDoc(collection(db, 'empresas'), {
        nombre: newEmpresa.nombre.trim(),
        codigo: newEmpresa.codigo.trim().toLowerCase(),
        createdAt: Timestamp.now(),
        createdBy: userEmail
      });

      setNewEmpresa({ nombre: '', codigo: '' });
      alert('Empresa agregada correctamente');
    } catch (error: any) {
      alert('Error al agregar empresa: ' + error.message);
    }
  };

  const handleEditEmpresa = (empresa: Empresa) => {
    setEditingEmpresa(empresa.id);
    setEditEmpresaData({ nombre: empresa.nombre, codigo: empresa.codigo });
  };

  const handleSaveEmpresa = async (empresaId: string) => {
    if (!editEmpresaData.nombre.trim() || !editEmpresaData.codigo.trim()) {
      alert('Por favor completa todos los campos');
      return;
    }

    try {
      await updateDoc(doc(db, 'empresas', empresaId), {
        nombre: editEmpresaData.nombre.trim(),
        codigo: editEmpresaData.codigo.trim().toLowerCase()
      });

      setEditingEmpresa(null);
      setEditEmpresaData({ nombre: '', codigo: '' });
      alert('Empresa actualizada correctamente');
    } catch (error: any) {
      alert('Error al actualizar empresa: ' + error.message);
    }
  };

  const handleDeleteEmpresa = async (empresaId: string, nombre: string) => {
    if (!window.confirm(`¿Estás seguro de eliminar la empresa "${nombre}"?\n\nEsto podría afectar a usuarios que ya tienen esta empresa asignada.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'empresas', empresaId));
      alert('Empresa eliminada correctamente');
    } catch (error: any) {
      alert('Error al eliminar empresa: ' + error.message);
    }
  };

  // Account deletion request handlers
  const handleApproveDeletion = async (request: AccountDeletionRequest) => {
    if (!window.confirm(`¿Aprobar eliminación de cuenta para ${request.username}? Esta acción no se puede deshacer.`)) {
      return;
    }

    if (!userEmail) {
      alert('Error: No se pudo determinar el usuario actual');
      return;
    }

    try {
      setProcessing(true);

      // Update deletion request status
      await updateDoc(doc(db, 'accountDeletionRequests', request.id), {
        status: 'approved',
        processedDate: Timestamp.now(),
        processedBy: userEmail
      });

      // TODO: Delete user from usuariosAutorizados - requires admin action
      alert(`Solicitud aprobada. IMPORTANTE: Debes eliminar manualmente al usuario "${request.username}" de la colección usuariosAutorizados en Firebase Console.`);
    } catch (error: any) {
      alert('Error al aprobar eliminación: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectDeletion = async (request: AccountDeletionRequest) => {
    const reason = window.prompt('¿Razón para rechazar la solicitud?');
    if (!reason) return;

    if (!userEmail) {
      alert('Error: No se pudo determinar el usuario actual');
      return;
    }

    try {
      setProcessing(false);

      await updateDoc(doc(db, 'accountDeletionRequests', request.id), {
        status: 'rejected',
        processedDate: Timestamp.now(),
        processedBy: userEmail,
        adminNotes: reason
      });

      alert('Solicitud rechazada');
    } catch (error: any) {
      alert('Error al rechazar solicitud: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const filteredRequests = requests.filter(req => {
    if (filter === 'ALL') return true;
    return req.status === filter;
  });

  const handleApprove = async (request: AccountRequest) => {
    if (!userEmail) {
      alert('Error: No se pudo determinar el usuario actual');
      return;
    }

    const confirmMsg = `¿Aprobar solicitud de ${request.firstName} ${request.lastName} (${request.email})?\n\nDespués de aprobar, debes crear manualmente la cuenta en Firebase Authentication y en la colección 'usuariosAutorizados'.`;

    if (!window.confirm(confirmMsg)) return;

    setProcessing(true);
    try {
      const requestRef = doc(db, 'accountRequests', request.id);
      await updateDoc(requestRef, {
        status: 'APPROVED',
        reviewedAt: Timestamp.now(),
        reviewedBy: userEmail,
        adminNotes: adminNotes || ''
      });

      alert(`✅ Solicitud aprobada.\n\nAcciones pendientes:\n1. Crear usuario en Firebase Authentication con email: ${request.email}\n2. Agregar documento en 'usuariosAutorizados' con email como ID\n3. Notificar al usuario por email a: ${request.email}`);

      setSelectedRequest(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Error al aprobar solicitud: ' + (error as Error).message);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (request: AccountRequest) => {
    if (!userEmail) {
      alert('Error: No se pudo determinar el usuario actual');
      return;
    }

    const reason = window.prompt('Motivo del rechazo (opcional):');
    if (reason === null) return; // User cancelled

    setProcessing(true);
    try {
      const requestRef = doc(db, 'accountRequests', request.id);
      await updateDoc(requestRef, {
        status: 'REJECTED',
        reviewedAt: Timestamp.now(),
        reviewedBy: userEmail,
        adminNotes: reason || adminNotes || 'Sin motivo especificado'
      });

      alert(`❌ Solicitud rechazada.\n\nSe recomienda notificar al usuario: ${request.email}`);

      setSelectedRequest(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Error al rechazar solicitud: ' + (error as Error).message);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, React.CSSProperties> = {
      PENDING: { backgroundColor: '#FFA726', color: 'white' },
      APPROVED: { backgroundColor: '#66BB6A', color: 'white' },
      REJECTED: { backgroundColor: '#EF5350', color: 'white' },
      CANCELLED: { backgroundColor: '#9E9E9E', color: 'white' }
    };

    return (
      <span style={{
        ...styles[status],
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {status}
      </span>
    );
  };

  const formatDate = (timestamp?: Timestamp) => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Cargando solicitudes...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '20px', color: '#1976d2' }}>
        📋 Solicitudes de Acceso, Empresas & Eliminaciones
      </h2>

      {/* Tabs */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', borderBottom: '2px solid #e0e0e0' }}>
        <button
          onClick={() => setActiveTab('requests')}
          style={{
            padding: '10px 20px',
            backgroundColor: 'transparent',
            color: activeTab === 'requests' ? '#1976d2' : '#666',
            border: 'none',
            borderBottom: activeTab === 'requests' ? '3px solid #1976d2' : 'none',
            cursor: 'pointer',
            fontWeight: activeTab === 'requests' ? 'bold' : 'normal',
            fontSize: '16px'
          }}
        >
          Solicitudes ({requests.filter(r => r.status === 'PENDING').length} pendientes)
        </button>
        <button
          onClick={() => setActiveTab('empresas')}
          style={{
            padding: '10px 20px',
            backgroundColor: 'transparent',
            color: activeTab === 'empresas' ? '#1976d2' : '#666',
            border: 'none',
            borderBottom: activeTab === 'empresas' ? '3px solid #1976d2' : 'none',
            cursor: 'pointer',
            fontWeight: activeTab === 'empresas' ? 'bold' : 'normal',
            fontSize: '16px'
          }}
        >
          Empresas ({empresas.length})
        </button>
        <button
          onClick={() => setActiveTab('deletions')}
          style={{
            padding: '10px 20px',
            backgroundColor: 'transparent',
            color: activeTab === 'deletions' ? '#d32f2f' : '#666',
            border: 'none',
            borderBottom: activeTab === 'deletions' ? '3px solid #d32f2f' : 'none',
            cursor: 'pointer',
            fontWeight: activeTab === 'deletions' ? 'bold' : 'normal',
            fontSize: '16px'
          }}
        >
          Eliminaciones ({deletionRequests.filter(r => r.status === 'pending').length} pendientes)
        </button>
      </div>

      {/* Tab Content: Requests */}
      {activeTab === 'requests' && (
        <>
          {/* Filter Buttons */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <button
          onClick={() => setFilter('PENDING')}
          style={{
            padding: '8px 16px',
            backgroundColor: filter === 'PENDING' ? '#FFA726' : '#f0f0f0',
            color: filter === 'PENDING' ? 'white' : '#333',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Pendientes ({requests.filter(r => r.status === 'PENDING').length})
        </button>
        <button
          onClick={() => setFilter('APPROVED')}
          style={{
            padding: '8px 16px',
            backgroundColor: filter === 'APPROVED' ? '#66BB6A' : '#f0f0f0',
            color: filter === 'APPROVED' ? 'white' : '#333',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Aprobadas ({requests.filter(r => r.status === 'APPROVED').length})
        </button>
        <button
          onClick={() => setFilter('REJECTED')}
          style={{
            padding: '8px 16px',
            backgroundColor: filter === 'REJECTED' ? '#EF5350' : '#f0f0f0',
            color: filter === 'REJECTED' ? 'white' : '#333',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Rechazadas ({requests.filter(r => r.status === 'REJECTED').length})
        </button>
        <button
          onClick={() => setFilter('ALL')}
          style={{
            padding: '8px 16px',
            backgroundColor: filter === 'ALL' ? '#1976d2' : '#f0f0f0',
            color: filter === 'ALL' ? 'white' : '#333',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Todas ({requests.length})
        </button>
      </div>

      {/* Requests Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Estado</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Nombre</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Email</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Empresa</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Cargo</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Fecha Solicitud</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                  No hay solicitudes {filter !== 'ALL' ? filter.toLowerCase() : ''}
                </td>
              </tr>
            ) : (
              filteredRequests.map(request => (
                <tr key={request.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '12px' }}>{getStatusBadge(request.status)}</td>
                  <td style={{ padding: '12px' }}>{request.firstName} {request.lastName}</td>
                  <td style={{ padding: '12px' }}>{request.email}</td>
                  <td style={{ padding: '12px' }}>{request.company}</td>
                  <td style={{ padding: '12px' }}>{request.position}</td>
                  <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>{formatDate(request.requestedAt)}</td>
                  <td style={{ padding: '12px' }}>
                    <button
                      onClick={() => setSelectedRequest(request)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Ver Detalles
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1976d2' }}>
              Detalles de Solicitud
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '12px', fontSize: '14px' }}>
                <strong>Estado:</strong>
                <div>{getStatusBadge(selectedRequest.status)}</div>

                <strong>Nombre:</strong>
                <div>{selectedRequest.firstName} {selectedRequest.lastName}</div>

                <strong>Email:</strong>
                <div>{selectedRequest.email}</div>

                <strong>Empresa:</strong>
                <div>{selectedRequest.company}</div>

                <strong>Cargo:</strong>
                <div>{selectedRequest.position}</div>

                {selectedRequest.phoneNumber && (
                  <>
                    <strong>Teléfono:</strong>
                    <div>{selectedRequest.phoneNumber}</div>
                  </>
                )}

                <strong>Fecha Solicitud:</strong>
                <div>{formatDate(selectedRequest.requestedAt)}</div>

                {selectedRequest.reviewedAt && (
                  <>
                    <strong>Fecha Revisión:</strong>
                    <div>{formatDate(selectedRequest.reviewedAt)}</div>
                  </>
                )}

                {selectedRequest.reviewedBy && (
                  <>
                    <strong>Revisado por:</strong>
                    <div>{selectedRequest.reviewedBy}</div>
                  </>
                )}
              </div>

              {selectedRequest.reason && (
                <div style={{ marginTop: '16px' }}>
                  <strong>Motivo:</strong>
                  <p style={{
                    marginTop: '8px',
                    padding: '12px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '6px',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    {selectedRequest.reason}
                  </p>
                </div>
              )}

              {selectedRequest.adminNotes && (
                <div style={{ marginTop: '16px' }}>
                  <strong>Notas del Administrador:</strong>
                  <p style={{
                    marginTop: '8px',
                    padding: '12px',
                    backgroundColor: '#fff3cd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    {selectedRequest.adminNotes}
                  </p>
                </div>
              )}
            </div>

            {selectedRequest.status === 'PENDING' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Notas del Administrador:
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Notas opcionales sobre esta solicitud..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    fontFamily: 'Arial, sans-serif',
                    resize: 'vertical'
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              {selectedRequest.status === 'PENDING' && (
                <>
                  <button
                    onClick={() => handleApprove(selectedRequest)}
                    disabled={processing}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: processing ? '#ccc' : '#66BB6A',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: processing ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    ✓ Aprobar
                  </button>
                  <button
                    onClick={() => handleReject(selectedRequest)}
                    disabled={processing}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: processing ? '#ccc' : '#EF5350',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: processing ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    ✗ Rechazar
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setSelectedRequest(null);
                  setAdminNotes('');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#9E9E9E',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* Tab Content: Empresas */}
      {activeTab === 'empresas' && (
        <div>
          <h3 style={{ marginBottom: '20px' }}>🏢 Gestión de Empresas</h3>

          {/* Add New Empresa Form */}
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '30px'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '15px' }}>Agregar Nueva Empresa</h4>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                  Nombre de la Empresa
                </label>
                <input
                  type="text"
                  value={newEmpresa.nombre}
                  onChange={(e) => setNewEmpresa({ ...newEmpresa, nombre: e.target.value })}
                  placeholder="Ej: Marbar S.A."
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                  Código (para la app)
                </label>
                <input
                  type="text"
                  value={newEmpresa.codigo}
                  onChange={(e) => setNewEmpresa({ ...newEmpresa, codigo: e.target.value.toLowerCase() })}
                  placeholder="Ej: marbar"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                />
                <small style={{ color: '#666', fontSize: '11px' }}>
                  Se usará en minúsculas. Sin espacios ni caracteres especiales.
                </small>
              </div>
              <button
                onClick={handleAddEmpresa}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  whiteSpace: 'nowrap'
                }}
              >
                + Agregar
              </button>
            </div>
          </div>

          {/* Empresas Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              backgroundColor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #ddd' }}>Nombre</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #ddd' }}>Código</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #ddd' }}>Creado Por</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #ddd' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {empresas.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: 40, textAlign: 'center', color: '#999' }}>
                      No hay empresas registradas. Agrega la primera empresa arriba.
                    </td>
                  </tr>
                ) : (
                  empresas.map(empresa => (
                    <tr key={empresa.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: 12 }}>
                        {editingEmpresa === empresa.id ? (
                          <input
                            type="text"
                            value={editEmpresaData.nombre}
                            onChange={(e) => setEditEmpresaData({ ...editEmpresaData, nombre: e.target.value })}
                            style={{
                              width: '100%',
                              padding: 6,
                              border: '1px solid #ddd',
                              borderRadius: 4
                            }}
                          />
                        ) : (
                          <strong>{empresa.nombre}</strong>
                        )}
                      </td>
                      <td style={{ padding: 12 }}>
                        {editingEmpresa === empresa.id ? (
                          <input
                            type="text"
                            value={editEmpresaData.codigo}
                            onChange={(e) => setEditEmpresaData({ ...editEmpresaData, codigo: e.target.value.toLowerCase() })}
                            style={{
                              width: '100%',
                              padding: 6,
                              border: '1px solid #ddd',
                              borderRadius: 4,
                              fontFamily: 'monospace'
                            }}
                          />
                        ) : (
                          <code style={{
                            backgroundColor: '#f0f0f0',
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 13
                          }}>
                            {empresa.codigo}
                          </code>
                        )}
                      </td>
                      <td style={{ padding: 12, fontSize: 13, color: '#666' }}>
                        {empresa.createdBy || 'Desconocido'}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        {editingEmpresa === empresa.id ? (
                          <>
                            <button
                              onClick={() => handleSaveEmpresa(empresa.id)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                marginRight: 8,
                                fontSize: 12
                              }}
                            >
                              ✓ Guardar
                            </button>
                            <button
                              onClick={() => {
                                setEditingEmpresa(null);
                                setEditEmpresaData({ nombre: '', codigo: '' });
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#999',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                fontSize: 12
                              }}
                            >
                              ✗ Cancelar
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEditEmpresa(empresa)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#2196F3',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                marginRight: 8,
                                fontSize: 12
                              }}
                            >
                              ✎ Editar
                            </button>
                            <button
                              onClick={() => handleDeleteEmpresa(empresa.id, empresa.nombre)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                fontSize: 12
                              }}
                            >
                              🗑 Eliminar
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{
            marginTop: 30,
            padding: 20,
            backgroundColor: '#e3f2fd',
            border: '1px solid #2196F3',
            borderRadius: 8
          }}>
            <h4 style={{ marginTop: 0 }}>ℹ️ Información Importante</h4>
            <ul style={{ marginBottom: 0, textAlign: 'left' }}>
              <li>El <strong>código</strong> debe ser único y se usará internamente en las apps móviles</li>
              <li>Usa códigos simples como: <code>marbar</code>, <code>tecpetrol</code>, <code>ypf</code></li>
              <li>Una vez creada la empresa, podrás asignarla a usuarios en la sección "Usuarios"</li>
              <li>Los códigos se guardan en minúsculas automáticamente</li>
            </ul>
          </div>
        </div>
      )}

      {/* Tab Content: Account Deletion Requests */}
      {activeTab === 'deletions' && (
        <div>
          <h3 style={{ marginBottom: '20px', color: '#d32f2f' }}>🗑️ Solicitudes de Eliminación de Cuenta</h3>

          <div style={{
            marginBottom: 20,
            padding: 20,
            backgroundColor: '#fff3e0',
            border: '1px solid #ff9800',
            borderRadius: 8
          }}>
            <h4 style={{ marginTop: 0 }}>⚠️ Proceso de Eliminación de Cuenta (Guideline 5.1.1(v))</h4>
            <ol style={{ marginBottom: 0, textAlign: 'left' }}>
              <li>Usuario solicita eliminación desde la app móvil (iOS/Android)</li>
              <li>Solicitud aparece aquí con estado "pending"</li>
              <li>Admin aprueba o rechaza la solicitud (plazo: menos de 48 horas)</li>
              <li>Si se aprueba, admin debe eliminar manualmente al usuario de Firebase Console</li>
              <li>Colecciones afectadas: <code>usuariosAutorizados</code> (email como document ID)</li>
            </ol>
          </div>

          {loading ? (
            <p>Cargando solicitudes...</p>
          ) : deletionRequests.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
              No hay solicitudes de eliminación
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                borderRadius: 8
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Usuario</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Fecha Solicitud</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Estado</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Procesado Por</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Fecha Procesado</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {deletionRequests.map((request) => (
                    <tr key={request.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '12px' }}>
                        <strong>{request.username}</strong>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {request.requestDate?.toDate().toLocaleDateString('es-AR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: 4,
                          fontSize: '12px',
                          fontWeight: 'bold',
                          backgroundColor:
                            request.status === 'pending' ? '#fff3cd' :
                            request.status === 'approved' ? '#d4edda' : '#f8d7da',
                          color:
                            request.status === 'pending' ? '#856404' :
                            request.status === 'approved' ? '#155724' : '#721c24'
                        }}>
                          {request.status === 'pending' ? '⏳ Pendiente' :
                           request.status === 'approved' ? '✅ Aprobada' : '❌ Rechazada'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {request.processedBy || '-'}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {request.processedDate?.toDate().toLocaleDateString('es-AR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        }) || '-'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {request.status === 'pending' && (
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleApproveDeletion(request)}
                              disabled={processing}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#d32f2f',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                cursor: processing ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                opacity: processing ? 0.6 : 1
                              }}
                            >
                              ✅ Aprobar
                            </button>
                            <button
                              onClick={() => handleRejectDeletion(request)}
                              disabled={processing}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#757575',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                cursor: processing ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                opacity: processing ? 0.6 : 1
                              }}
                            >
                              ❌ Rechazar
                            </button>
                          </div>
                        )}
                        {request.status !== 'pending' && (
                          <span style={{ color: '#999', fontSize: '12px' }}>
                            {request.adminNotes || 'Procesada'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
